/**
 * QuoteSection — mutations route through event-workspace-api: sendToClient,
 * sendQuoteReminder.
 */
import { Fragment, useMemo, useState } from "react";
import { SectionCard } from "../ui/SectionCard";
import {
  ResponsiveTable,
  TableBody,
  TableCell,
  TableFullWidthCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../ui/ResponsiveTable";
import { Tooltip } from "../ui/Tooltip";
import { Button } from "../ui/Button";
import { ConfirmModal } from "../ui/ConfirmModal";
import { FolderTab, FolderTabBar } from "../ui/FolderTabs";
import { formatTimestamp } from "../../lib/format-utils";
import {
  artifactSummaryLine,
  packageQuotePdfUrl,
  useOptimisticMask,
} from "../../lib/generated-artifacts";
import {
  MUTATION_KEYS,
  type MutationRunner,
} from "../../lib/use-event-workspace";
import { sendQuoteReminder, sendToClient } from "../../lib/event-workspace-api";
import {
  formatNZD,
  gstOf,
  grossUpInclGST,
  depositFromPercent,
} from "@shared/nz-money";
import mask from "./mask.module.css";
import styles from "./QuoteSection.module.css";

interface QuoteLineItem {
  id?: string;
  description: string;
  quantity: number;
  days: number;
  unit_rate: number;
  line_total: number;
  type?: string;
  sort_order: number;
  // Grouped quote fields (#21): resolved heading (null on tail lines),
  // flat $ discount already netted off line_total, note under the line.
  group_heading?: string | null;
  discount?: number;
  line_note?: string | null;
  // Per-section summarise snapshot: the group renders collapsed
  // (heading + total, no item breakdown) when its lines carry this.
  group_summarised?: boolean;
  // Quote package this snapshot line belongs to (#20).
  package_key?: string | null;
}

/** One quote package (#20) shipped on the workspace read. */
interface QuoteSectionPackage {
  key: string;
  name: string;
  position: number;
  draft_pdf_url?: string | null;
  sent_pdf_url?: string | null;
}

/**
 * One partition's line table (#20): the grouped line rows (heading order
 * matching generation), the percent-discount tail, and the partition's
 * subtotal/GST/total block.
 */
function QuoteLinesTable({ items }: { items: QuoteLineItem[] }) {
  const groupedLineItems = useMemo(() => {
    const groups: {
      heading: string;
      items: { item: QuoteLineItem; index: number }[];
      total: number;
      // Collapses only when EVERY line in the group carries the snapshot —
      // mirrors the PDF renderer.
      summarised: boolean;
    }[] = [];
    const idx = new Map<string, number>();
    items.forEach((item, index) => {
      if (item.type === "gear_discount") return;
      const heading = item.group_heading?.trim() || "Other";
      let g = idx.get(heading);
      if (g == null) {
        g = groups.length;
        idx.set(heading, g);
        groups.push({ heading, items: [], total: 0, summarised: true });
      }
      groups[g].items.push({ item, index });
      groups[g].total += Number(item.line_total) || 0;
      if (!item.group_summarised) groups[g].summarised = false;
    });
    return groups;
  }, [items]);

  const discountLines = useMemo(
    () =>
      items
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => item.type === "gear_discount"),
    [items],
  );

  const subtotal = useMemo(
    () => items.reduce((sum, item) => sum + (Number(item.line_total) || 0), 0),
    [items],
  );
  const gst = gstOf(subtotal);
  const total = grossUpInclGST(subtotal);

  return (
    <>
      <ResponsiveTable>
        <TableHead>
          <TableHeaderCell>Description</TableHeaderCell>
          <TableHeaderCell align="right" width="14">
            Qty
          </TableHeaderCell>
          <TableHeaderCell align="right" width="14">
            Days
          </TableHeaderCell>
          <TableHeaderCell align="right" width="20">
            Rate
          </TableHeaderCell>
          <TableHeaderCell align="right" width="24">
            Total
          </TableHeaderCell>
        </TableHead>
        <TableBody>
          {groupedLineItems.map((group) => (
            <Fragment key={group.heading}>
              {group.summarised ? (
                // Summarised section: the client sees the heading + section
                // price with a plain item list (no per-line pricing or
                // notes) — mirror that face here.
                <>
                  <TableRow variant="plain">
                    <TableCell
                      colSpan={4}
                      padding="group"
                      className={styles.groupHeading}
                    >
                      {group.heading}
                    </TableCell>
                    <TableCell
                      label="Total"
                      align="right"
                      padding="group"
                      className={styles.groupHeadingTotal}
                    >
                      {formatNZD(group.total)}
                    </TableCell>
                  </TableRow>
                  {group.items.every(({ item }) => item.type === "service") ? (
                    // All-labour group: the client sees total hours only,
                    // never the setup/pack-down/travel build-up (#181).
                    <TableRow variant="plain">
                      <TableFullWidthCell
                        colSpan={5}
                        padding="compact"
                        className={styles.summarisedLine}
                      >
                        {group.items.reduce(
                          (sum, { item }) => sum + (Number(item.quantity) || 0),
                          0,
                        )}{" "}
                        hours total
                      </TableFullWidthCell>
                    </TableRow>
                  ) : (
                    group.items.map(({ item, index }) => (
                      <TableRow key={index} variant="plain">
                        <TableFullWidthCell
                          colSpan={5}
                          padding="compact"
                          className={styles.summarisedLine}
                        >
                          {item.description}
                          {item.quantity > 1 ? ` ×${item.quantity}` : ""}
                        </TableFullWidthCell>
                      </TableRow>
                    ))
                  )}
                </>
              ) : (
                <>
                  <TableRow variant="plain">
                    <TableFullWidthCell
                      colSpan={5}
                      padding="group"
                      className={styles.groupHeadingOpen}
                    >
                      {group.heading}
                    </TableFullWidthCell>
                  </TableRow>
                  {group.items.map(({ item, index }) => (
                    // Mobile: description full-width over the numeric columns
                    // in a compact 2×2 grid. sm+ is the plain table.
                    <TableRow key={index}>
                      <TableCell
                        label="Description"
                        span={4}
                        className={styles.description}
                      >
                        {item.description}
                        {item.line_note && (
                          <span className={styles.lineNote}>
                            {item.line_note}
                          </span>
                        )}
                      </TableCell>
                      <TableCell label="Qty" align="right">
                        {item.quantity}
                      </TableCell>
                      <TableCell label="Days" align="right">
                        {item.days}
                      </TableCell>
                      <TableCell label="Rate" align="right">
                        {formatNZD(item.unit_rate)}
                      </TableCell>
                      <TableCell
                        label="Total"
                        align="right"
                        className={styles.money}
                      >
                        {formatNZD(item.line_total)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow variant="plain">
                    <TableFullWidthCell
                      colSpan={5}
                      padding="compact"
                      className={styles.groupTotal}
                    >
                      Total for {group.heading}: {formatNZD(group.total)}
                    </TableFullWidthCell>
                  </TableRow>
                </>
              )}
            </Fragment>
          ))}
          {discountLines.map(({ item, index }) => {
            const abs = Math.abs(Number(item.line_total) || 0);
            return (
              <TableRow key={index} className={styles.discountRow}>
                <TableCell
                  label="Description"
                  span={4}
                  colSpan={4}
                  className={styles.discountDescription}
                >
                  {item.description}
                </TableCell>
                <TableCell
                  label="Total"
                  span={4}
                  align="right"
                  className={styles.money}
                >
                  −{formatNZD(abs)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </ResponsiveTable>

      {items.length > 0 && (
        <div className={styles.totals}>
          <p className={styles.totalsLine}>Subtotal: {formatNZD(subtotal)}</p>
          {/* A span, not a <p>: Astryx renders the tooltip bubble as a div in
              place, which would close a <p> early. */}
          <span className={styles.totalsLineBlock}>
            <Tooltip content="Calculated as 15% of the quote subtotal.">
              GST (15%)
            </Tooltip>
            : {formatNZD(gst)}
          </span>
          <p className={styles.grandTotal}>
            Total (incl. GST): {formatNZD(total)}
          </p>
        </div>
      )}
    </>
  );
}

interface QuoteSectionProps {
  bookingId: string;
  quoteNumber: string;
  lineItems: QuoteLineItem[];
  /** The booking's quote packages (#20); empty = classic single quote. */
  quotePackages?: QuoteSectionPackage[];
  quoteSentAt: string | null;
  quoteGeneratedAt: string | null;
  quoteReminderSentAt: string | null;
  isApproved: boolean;
  /** Typed-name e-signature from the approval ceremony; null on legacy
   *  approvals that predate it. */
  approvedSignerName?: string | null;
  /** client_approvals.client_approved_at — shown beside the signer name. */
  clientApprovedAt?: string | null;
  state: "active" | "complete" | "future";
  isStale?: boolean;
  /** Workspace mutation runner — owns busy state, refetch, and the error sink. */
  mutations: MutationRunner;
  depositPercent: string;
  quoteValidDays: string;
  notes: string;
  open?: boolean;
  onToggle?: () => void;
  sectionRef?: React.RefObject<HTMLDivElement | null>;
  headerAction?: React.ReactNode;
}

export function QuoteSection({
  bookingId,
  quoteNumber,
  lineItems,
  quotePackages = [],
  quoteSentAt,
  quoteGeneratedAt,
  quoteReminderSentAt,
  isApproved,
  approvedSignerName = null,
  clientApprovedAt = null,
  state,
  isStale = false,
  mutations,
  depositPercent,
  quoteValidDays,
  notes,
  open,
  onToggle,
  sectionRef,
  headerAction,
}: QuoteSectionProps) {
  const sending = mutations.isPending(MUTATION_KEYS.sendQuote);
  const sendingReminder = mutations.isPending(MUTATION_KEYS.sendQuoteReminder);
  const [showStaleConfirm, setShowStaleConfirm] = useState(false);
  const [showResendConfirm, setShowResendConfirm] = useState(false);
  const { showMask, markSent } = useOptimisticMask({
    sentAt: quoteSentAt,
    generatedAt: quoteGeneratedAt,
  });

  // Package partitions (#20): each package renders its own complete line set
  // (rows with no package_key count in every partition); a no-packages quote
  // is one anonymous partition — exactly the pre-package rendering.
  const partitions = useMemo(() => {
    if (quotePackages.length === 0) {
      return [
        {
          key: null as string | null,
          name: null as string | null,
          items: lineItems,
        },
      ];
    }
    return [...quotePackages]
      .sort((a, b) => a.position - b.position)
      .map((p) => ({
        key: p.key as string | null,
        name: p.name as string | null,
        items: lineItems.filter(
          (it) => it.package_key == null || it.package_key === p.key,
        ),
      }));
  }, [lineItems, quotePackages]);

  // The card's headline money is package 1's (the committed quote total).
  const subtotal = useMemo(
    () =>
      (partitions[0]?.items ?? []).reduce(
        (sum, item) => sum + (Number(item.line_total) || 0),
        0,
      ),
    [partitions],
  );
  const gst = useMemo(() => gstOf(subtotal), [subtotal]);
  const total = useMemo(() => grossUpInclGST(subtotal), [subtotal]);

  // Package tab selection (#20) — display-only; defaults to package 1.
  const [activePackageKey, setActivePackageKey] = useState<string | null>(
    partitions[0]?.key ?? null,
  );
  const activePartition =
    partitions.find((p) => p.key === activePackageKey) ?? partitions[0];

  const handleSendClick = () => {
    if (isStale) {
      setShowStaleConfirm(true);
      return;
    }
    if (isApproved) {
      setShowResendConfirm(true);
      return;
    }
    void handleSendToClient();
  };

  const handleSendToClient = async () => {
    await mutations.run(
      MUTATION_KEYS.sendQuote,
      "Send quote",
      () =>
        sendToClient(
          bookingId,
          notes || null,
          depositPercent === "" ? 0 : Number(depositPercent),
          quoteValidDays === "" ? 28 : Number(quoteValidDays),
        ),
      // Mask the card as "sent" before the refetch confirms it.
      { onSuccess: markSent },
    );
  };

  const summary = artifactSummaryLine({
    isStale,
    staleLine: `Quote #${quoteNumber} is out of date, regenerate from Quote Details above`,
    showMask,
    maskLine: isApproved
      ? `Quote #${quoteNumber} approved, regenerate to update`
      : `Quote #${quoteNumber} sent, regenerate to update`,
    contentLine: isApproved
      ? `Quote #${quoteNumber} approved, ${formatNZD(total)}`
      : quoteSentAt
        ? `Quote #${quoteNumber} sent, ${formatNZD(total)}`
        : lineItems.length > 0
          ? formatNZD(total)
          : null,
  });

  return (
    <SectionCard
      title="Quote Summary"
      collapsible
      open={open}
      onToggle={onToggle}
      sectionRef={sectionRef}
      state={state}
      isStale={isStale}
      summary={summary}
      headerAction={headerAction}
    >
      <div className={mask.masked}>
        {showMask && (
          <div className={mask.mask}>
            <p className={mask.text}>
              {isApproved
                ? approvedSignerName
                  ? `Quote #${quoteNumber} approved — signed by ${approvedSignerName}${
                      clientApprovedAt
                        ? ` on ${formatTimestamp(clientApprovedAt)}`
                        : ""
                    }.`
                  : `Quote #${quoteNumber} has been approved by the client.`
                : `Quote #${quoteNumber} sent to client.`}
              <br />
              To send updated details, edit Quote Details above and click
              &quot;Generate Quote Summary&quot;.
            </p>
            {(quoteReminderSentAt || quoteSentAt) && (
              <p className={mask.sentAt}>
                {quoteReminderSentAt
                  ? `Reminder sent ${formatTimestamp(quoteReminderSentAt)}`
                  : `Sent ${formatTimestamp(quoteSentAt!)}`}
              </p>
            )}
            {!isApproved && (
              <Button
                onClick={() =>
                  mutations.run(
                    MUTATION_KEYS.sendQuoteReminder,
                    "Send quote reminder",
                    () => sendQuoteReminder(bookingId),
                  )
                }
                variant="outline"
                loading={sendingReminder}
              >
                Send Reminder to Client
              </Button>
            )}
          </div>
        )}

        {quotePackages.length > 0 && (
          <FolderTabBar ariaLabel="Quote packages" wrap className={styles.tabs}>
            {partitions.map((partition) => (
              <FolderTab
                key={partition.key ?? "single"}
                selected={partition.key === activePartition?.key}
                surface="card"
                onSelect={() => setActivePackageKey(partition.key)}
              >
                {partition.name}
              </FolderTab>
            ))}
          </FolderTabBar>
        )}

        {activePartition && (
          <div key={activePartition.key ?? "single"}>
            {activePartition.name != null &&
              (() => {
                const pkg = quotePackages.find(
                  (p) => p.key === activePartition.key,
                );
                const url = pkg ? packageQuotePdfUrl(pkg) : null;
                return url ? (
                  <div className={styles.pdfLink}>
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className={styles.pdf}
                    >
                      Quote PDF
                    </a>
                  </div>
                ) : null;
              })()}
            <QuoteLinesTable items={activePartition.items} />
          </div>
        )}

        {lineItems.length > 0 && !showMask && (
          <div className={styles.terms}>
            <div className={styles.termsGrid}>
              <div>
                <p className={styles.termLabel}>Deposit</p>
                <p className={styles.termValue}>
                  {depositPercent === "" ? "0" : depositPercent}%,{" "}
                  {formatNZD(
                    depositFromPercent(
                      subtotal,
                      depositPercent === "" ? 0 : Number(depositPercent),
                    ),
                  )}
                </p>
              </div>
              <div>
                <p className={styles.termLabel}>Valid for</p>
                <p className={styles.termValue}>{quoteValidDays || "28"} days</p>
              </div>
            </div>
            {notes && (
              <div className={styles.notes}>
                <p className={styles.termLabel}>Notes for client</p>
                <p className={styles.termValue}>{notes}</p>
              </div>
            )}
            <Button
              onClick={handleSendClick}
              disabled={lineItems.length === 0}
              loading={sending}
              size="lg"
              className={styles.fullWidth}
            >
              {quoteSentAt ? "Resend Updated Quote" : "Send to Client"}
            </Button>
          </div>
        )}
      </div>
      <ConfirmModal
        open={showStaleConfirm}
        title="Quote is out of date"
        message={
          <>
            You&apos;ve edited the quote details since this PDF was generated.
            The client would receive the <strong>old</strong> figures.
            Regenerate the quote summary first?
          </>
        }
        confirmLabel="Send anyway"
        confirmVariant="danger"
        loading={sending}
        onConfirm={() => {
          if (sending || lineItems.length === 0) return;
          setShowStaleConfirm(false);
          if (isApproved) {
            setShowResendConfirm(true);
            return;
          }
          void handleSendToClient();
        }}
        onCancel={() => {
          if (!sending) setShowStaleConfirm(false);
        }}
      />
      <ConfirmModal
        open={showResendConfirm}
        title="Client will need to re-approve"
        message={
          <>
            The client previously approved Quote #{quoteNumber}. Resending will{" "}
            <strong>clear their approval</strong> and require them to approve
            the new quote on its own merits.
            <br />
            <br />
            If a deposit was already paid against the previous quote, the
            recorded payment status will reset to pending. You&apos;ll need to
            reconcile any prior payment manually before re-issuing the deposit
            invoice.
          </>
        }
        confirmLabel="Resend and reset approval"
        confirmVariant="danger"
        loading={sending}
        onConfirm={() => {
          if (sending || lineItems.length === 0) return;
          setShowResendConfirm(false);
          void handleSendToClient();
        }}
        onCancel={() => {
          if (!sending) setShowResendConfirm(false);
        }}
      />
    </SectionCard>
  );
}
